// components/TrialBanner.tsx
// Banner to display trial account status and countdown

import React from 'react';
import { useClient } from '../contexts/ClientContext';
import { useParams, useNavigate } from 'react-router-dom';

const TrialBanner: React.FC = () => {
  const { client } = useClient();
  const { company } = useParams<{ company: string }>();
  const navigate = useNavigate();
  const [sidebarWidth, setSidebarWidth] = React.useState<'16rem' | '4.5rem'>(() => {
    if (typeof window === 'undefined') return '16rem';
    return localStorage.getItem('sidebar:collapsed') === '1' ? '4.5rem' : '16rem';
  });

  // Listen for sidebar width changes
  React.useEffect(() => {
    const updateWidth = () => {
      const collapsed = localStorage.getItem('sidebar:collapsed') === '1';
      setSidebarWidth(collapsed ? '4.5rem' : '16rem');
    };
    window.addEventListener('sidebar:width', updateWidth);
    window.addEventListener('focus', updateWidth);
    return () => {
      window.removeEventListener('sidebar:width', updateWidth);
      window.removeEventListener('focus', updateWidth);
    };
  }, []);

  const trialInfo = client?.trialInfo;

  // Only show banner if trial is active and not expired
  if (!trialInfo || !trialInfo.isTrial || trialInfo.isExpired) {
    return null;
  }

  const { daysUsed, daysLeft, daysTotal, trialExpiresAt } = trialInfo;

  // Calculate progress percentage
  const progressPercent = Math.min(100, (daysUsed / daysTotal) * 100);

  // Format expiry date
  const expiryDate = trialExpiresAt
    ? new Date(trialExpiresAt).toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  // Check if trial was extended (if trialEndDate is more than 14 days from trialStartDate)
  const wasExtended = trialInfo.trialStartedAt && trialExpiresAt && (() => {
    const start = new Date(trialInfo.trialStartedAt);
    const end = new Date(trialExpiresAt);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 14;
  })();

  // Calculate extension days
  const extensionDays = wasExtended && trialInfo.trialStartedAt && trialExpiresAt ? (() => {
    const start = new Date(trialInfo.trialStartedAt);
    const end = new Date(trialExpiresAt);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays - 14;
  })() : 0;

  return (
    <div
      className="fixed z-40 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-b border-amber-200/60 shadow-sm backdrop-blur-sm"
      style={{
        top: '55px', // Below header (56px on desktop, 48px on mobile - adjust if needed)
        left: 0, // Start from left edge (main content area)
        right: typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
          ? sidebarWidth
          : 0, // End before sidebar on desktop, full width on mobile
        paddingTop: 'env(safe-area-inset-top, 0px)',
        minHeight: 'fit-content',
      }}
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 md:py-3 pb-4 sm:pb-5 md:pb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 md:gap-4 rtl:flex-row-reverse">
          <div className="flex-1 min-w-0 text-right w-full sm:w-auto">
            <div className="flex items-center gap-2 sm:gap-2.5 mb-1.5 sm:mb-2 flex-row-reverse flex-wrap">
              <span className="inline-flex items-center px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold bg-amber-200/80 text-amber-900 whitespace-nowrap shadow-sm border border-amber-300/30">
                Demo Account
              </span>
              <span className="text-[11px] sm:text-xs md:text-sm text-amber-900 font-semibold whitespace-nowrap">
                {daysUsed} מתוך {daysTotal} ימים
              </span>
            </div>
            <p className="text-[11px] sm:text-xs md:text-sm text-amber-900 leading-relaxed text-right break-words font-medium mb-1">
              {expiryDate ? (
                <>התקופה הניסיונית תסתיים ב-<span className="font-semibold">{expiryDate}</span>.</>
              ) : (
                <>התקופה הניסיונית תסתיים בעוד <span className="font-semibold">{daysLeft}</span> ימים.</>
              )}
              {daysLeft <= 3 && daysLeft > 0 && (
                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-bold bg-red-100 text-red-700 border border-red-200 animate-pulse">זמן נותר מוגבל!</span>
              )}
            </p>
            {wasExtended && extensionDays > 0 && (
              <p className="text-[11px] sm:text-xs text-green-800 mt-1.5 font-semibold bg-green-50/90 px-2.5 sm:px-3 py-1.5 rounded-lg border border-green-300/50 text-right break-words shadow-sm">
                <span className="inline-block ml-1">✓</span> תקופת הניסיון הוארכה ב-{extensionDays} ימים נוספים. התקופה המקורית הסתיימה ונוספו {extensionDays} ימים.
              </p>
            )}
            <p className="text-[10px] sm:text-xs text-amber-700/90 mt-1.5 text-right break-words leading-relaxed">
              לאחר מכן, החשבון ינעל עד לבחירת תוכנית בתשלום. הנתונים לא ימחקו.
            </p>
            {/* Progress bar */}
            <div className="mt-2 sm:mt-2.5 w-full bg-amber-200/40 rounded-full h-1.5 sm:h-2 overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-l from-amber-500 via-amber-600 to-amber-700 transition-all duration-500 ease-out shadow-sm"
                style={{ 
                  width: `${progressPercent}%`, 
                  marginRight: 0,
                  marginLeft: 'auto',
                  borderRadius: '9999px'
                }}
              />
            </div>
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto mt-2 sm:mt-0">
            <button
              onClick={() => {
                // Navigate to contact/support or upgrade page if available
                // For now, just scroll or show info
                window.open('mailto:support@wjsinnovation.com?subject=Upgrade Trial Account', '_blank');
              }}
              className="w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 border border-blue-800/30 rounded-lg transition-all duration-200 whitespace-nowrap shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
            >
              צור קשר
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to get banner height for spacing
export const useTrialBannerHeight = (): number => {
  const { client } = useClient();
  const trialInfo = client?.trialInfo;

  // Only count height if banner is visible
  if (!trialInfo || !trialInfo.isTrial || trialInfo.isExpired) {
    return 0;
  }

  // Calculate approximate height based on content:
  // - Top padding: py-2 sm:py-2.5 md:py-3 = 8px/10px/12px
  // - Bottom padding: pb-3 sm:pb-4 md:pb-5 = 12px/16px/20px
  // - Badge + days: ~24px
  // - Main text: ~20px
  // - Progress bar: ~8px
  // - Bottom spacing: ~16px (increased for more space)
  // Total: ~88-116px depending on screen size
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const isTablet = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
  
  if (isMobile) {
    return 120; // Mobile: more padding + extra space
  } else if (isTablet) {
    return 110; // Tablet: medium padding + extra space
  } else {
    return 105; // Desktop: standard padding + extra space
  }
};

export default TrialBanner;

